import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API } from "@/lib/api";

// Fires a fire-and-forget POST /api/track/pageview whenever the route changes.
// GDPR-friendly: no cookies, no identifiers, server hashes IP+UA daily.
// Admin paths are skipped both here and on the server side.
//
// Debounce + last-fired guard together kill React 18 StrictMode double-mounts
// and any transient i18n redirects that happen within a few hundred ms.
export default function PageviewTracker() {
  const { pathname } = useLocation();
  const lastPath = useRef("");
  const lastFireAt = useRef(0);

  useEffect(() => {
    if (!pathname) return;
    if (pathname.startsWith("/admin")) {
      lastPath.current = pathname;
      return;
    }
    if (pathname === lastPath.current) return;

    const timer = setTimeout(() => {
      // Only fire for the CURRENT window path — protects against transient
      // route changes that were resolved by a later redirect.
      const currentPath = window.location.pathname;
      if (currentPath !== pathname) return;
      if (currentPath === lastPath.current) return;
      const now = Date.now();
      if (now - lastFireAt.current < 400) return;

      lastPath.current = currentPath;
      lastFireAt.current = now;

      const seg = currentPath.split("/").filter(Boolean)[0] || "";
      const lang = ["en", "fr"].includes(seg) ? seg : "de";

      try {
        const body = JSON.stringify({
          path: currentPath,
          lang,
          referrer: document.referrer || null,
        });
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: "application/json" });
          navigator.sendBeacon(`${API}/track/pageview`, blob);
        } else {
          fetch(`${API}/track/pageview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch (_) {
        /* never break the app because of analytics */
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}
